import {
  CLEAR_TIMEOUT,
  SET_TIMEOUT,
  TIMEOUT_TICK,
  timerWorkerScript
} from './TimerWorker';
import { getLocalParticipant } from 'store/modules/base/participants/functions';
import { setBlurContentWarning } from 'store/modules/large-video/actions';
import { getSignedInUser } from 'store/modules/login/functions';

export default class YoloEffect {
  _model;
  _stream;
  _outputCanvasElement;
  _selfCanvasElement;
  _videoScale;
  _resumeTimeout;
  _prediction = true;
  _blur = false;
  constructor(model, track) {
    this._model = model;
    this._stream = track;

    // Bind event handler so it is only bound once for every instance.
    this._onMaskFrameTimer = this._onMaskFrameTimer.bind(this);

    // Workaround for FF issue https://bugzilla.mozilla.org/show_bug.cgi?id=1388974
    this._selfCanvasElement = document.getElementById('selfcanvas');
    this._outputCanvasElement = document.createElement('canvas');
    this._outputCanvasElement.getContext('2d');
    this._inputVideoElement = document.createElement('video');
    // this.startEffect();
  }

  /**
   * EventHandler onmessage for the maskFrameTimerWorker WebWorker.
   *
   * @private
   * @param {EventHandler} response - The onmessage EventHandler parameter.
   * @returns {void}
   */
  _onMaskFrameTimer(response) {
    if (response.data.id === TIMEOUT_TICK) {
      this._renderMask();
    }
  }

  /**
   * Checks if the local track supports this effect.
   *
   * @param {JitsiLocalTrack} jitsiLocalTrack - Track to apply effect.
   * @returns {boolean} - Returns true if this effect can run on the specified track
   * false otherwise.
   */
  isEnabled(jitsiLocalTrack) {
    return (
      jitsiLocalTrack.isVideoTrack() && jitsiLocalTrack.videoType === 'camera'
    );
  }

  /**
   * Loop function to render the background mask.
   *
   * @private
   * @returns {void}
   */
  _renderMask() {
    this.runInference();
    this.runPostProcessing();

    this._maskFrameTimerWorker.postMessage({
      id: SET_TIMEOUT,
      timeMs: 50
    });
  }

  /**
   * Represents the run post processing.
   *
   * @returns {void}
   */
  startEffect(stream) {
    this._stream = stream;
    this._maskFrameTimerWorker = new Worker(timerWorkerScript, {
      name: 'Sensoring effect worker'
    });
    this._maskFrameTimerWorker.onmessage = this._onMaskFrameTimer;
    const track = this._stream.getVideoTracks()[0];
    const { width, frameRate, height } = track.getSettings
      ? track.getSettings()
      : track.getConstraints();
    const { clientWidth, clientHeight } =
      // eslint-disable-next-line no-undef
      APP.store.getState()['features/base/responsive-ui'];
    this._videoScale = clientHeight / height;
    this._outputCanvasElement.width = width;
    this._outputCanvasElement.height = height;
    this._selfCanvasElement = document.getElementById('selfcanvas');
    this._outputCanvasCtx = this._outputCanvasElement.getContext('2d');
    this._inputVideoElement.width = parseInt(width, 10);
    this._inputVideoElement.height = parseInt(height, 10);
    this._inputVideoElement.autoplay = true;
    this._inputVideoElement.srcObject = this._stream;
    if (this._selfCanvasElement) {
      this._selfCanvasElement.width = clientWidth;
      this._selfCanvasElement.height = clientHeight;
      this._selfCanvasCtx = this._selfCanvasElement?.getContext('2d');
    }
    this._inputVideoElement.onloadeddata = () => {
      this._maskFrameTimerWorker.postMessage({
        id: SET_TIMEOUT,
        timeMs: 1000 / 30
      });
    };
    return this._outputCanvasElement.captureStream(parseInt(frameRate, 10));
  }

  drawRect(x, y, width, height, color, object, score, occupied) {
    if (this._selfCanvasCtx) {
      // Draw bounding box
      this._selfCanvasCtx.beginPath();
      this._selfCanvasCtx.rect(x, y, width, height);
      this._selfCanvasCtx.lineWidth = 2;
      this._selfCanvasCtx.strokeStyle = color;
      this._selfCanvasCtx.fillStyle = color;
      this._selfCanvasCtx.stroke();
      this._selfCanvasCtx.fillText(
        `${object}: ${score.toFixed(2)} ${
          occupied ? `(${occupied.toFixed(2)}%)` : ''
        }`,
        x,
        y > 10 ? y - 5 : 10
      );
    }
  }
  /**
   * Represents the run Tensorflow Interference.
   *
   * @returns {void}
   */
  async runInference() {
    // eslint-disable-next-line no-undef
    const options = getSignedInUser(APP.store.getState()).device?.options;
    const { clientWidth, clientHeight } =
      // eslint-disable-next-line no-undef
      APP.store.getState()['features/base/responsive-ui'];
    const frameArea = clientWidth * clientHeight;
    const predictions = await this._model.detect(this._inputVideoElement);
    if (this._selfCanvasCtx && this._selfCanvasElement) {
      this._selfCanvasCtx.clearRect(
        0,
        0,
        this._selfCanvasElement.width,
        this._selfCanvasElement.height
      );
    }

    const signal = [];

    predictions.forEach((prediction) => {
      const { bbox, class: _c, score } = prediction;
      const [x, y, width, height] = bbox;
      const area = width * this._videoScale * (height * this._videoScale);

      let color = 'red';
      let occupied = area / (frameArea / 100);

      if (options?.censorshipRules?.length > 0) {
        const _ruleIndex = options?.censorshipRules.findIndex(
          (r) => r.class === _c
        );
        if (_ruleIndex !== -1) {
          if (
            area >
              (frameArea / 100) * options?.censorshipRules[_ruleIndex].limit &&
            score >= options?.censorshipRules[_ruleIndex].accuracy / 100
          ) {
            color = 'red';
            signal.push({
              ...prediction,
              frameArea,
              occupied: area
            });
          } else {
            color = 'green';
          }
        } else {
          color = 'yellow';
        }
      }
      this.drawRect(
        x * this._videoScale,
        y * this._videoScale,
        width * this._videoScale,
        height * this._videoScale,
        color,
        _c,
        score,
        occupied
      );
    });

    if (this._prediction) {
      if (signal.length > 0) {
        // console.log('predictions', signal);
        this.blur(options.censorshipPeriod);
      } else {
        this.unblur();
      }
    }
  }

  blur(timeout = 5) {
    this._prediction = false;
    this._blur = true;
    // eslint-disable-next-line no-undef
    APP.store.dispatch(
      // eslint-disable-next-line no-undef
      setBlurContentWarning(getLocalParticipant(APP.store.getState()).id)
    );
    if (this._resumeTimeout) clearTimeout(this._resumeTimeout);
    this._resumeTimeout = setTimeout(() => {
      this._prediction = true;
    }, timeout * 1000);
  }

  unblur() {
    this._blur = false;
    // eslint-disable-next-line no-undef
    APP.store.dispatch(setBlurContentWarning(undefined));
  }

  /**
   * Represents the run post processing.
   *
   * @returns {void}
   */
  runPostProcessing() {
    const track = this._stream.getVideoTracks()[0];
    const { height, width } = track.getSettings() ?? track.getConstraints();

    if (!this._outputCanvasCtx) {
      return;
    }

    this._outputCanvasElement.height = height;
    this._outputCanvasElement.width = width;
    // this._outputCanvasCtx.globalCompositeOperation = 'copy';

    this._outputCanvasCtx?.drawImage(
      this._inputVideoElement,
      0,
      0,
      width,
      height
    );
    // Apply blur filter if enabled
    if (this._blur) {
      // Draw the background.
      // this._outputCanvasCtx.globalCompositeOperation = 'destination-over';
      this._outputCanvasCtx.filter = `blur(50px)`;
      this._outputCanvasElement.style.backdropFilter = 'blur(50px)';
      this._outputCanvasCtx?.drawImage(
        this._outputCanvasElement,
        0,
        0,
        width,
        height
      );
      this._outputCanvasCtx.filter = 'none'; //Reset filter
    }
  }

  /**
   * Stops the capture and render loop.
   *
   * @returns {void}
   */
  stopEffect() {
    this._maskFrameTimerWorker.postMessage({
      id: CLEAR_TIMEOUT
    });

    this._maskFrameTimerWorker.terminate();
  }
}
